import { useContext } from "preact/hooks";
import type { LiveSheetAbility, LiveSheetRitual } from "../../types/generated/live";
import { Box } from "../ui/box";
import { Icon, MaskIcon, talentIconUrl } from "../ui/icon";
import { RollChip } from "../ui/roll_chip";
import { Term } from "../ui/term";
import { Button, HelpLink } from "./controls";
import { EditContext } from "./edits";
import { foldOf } from "./folds";
import { choiceText } from "./format";
import { interpolate, type Labels } from "./labels";
import { firstPendingLevel, isRace, pendingList } from "./level_up";
import { useOpenPopup } from "./popups";
import { RuleText } from "./rule_text";
import { testRoll } from "./rolls";
import type { SheetView } from "./sheet_view";
import { SpellName, Spells } from "./spells";
import { talentTiles } from "./talents";
import { type Spent, spentSet, Uses } from "./uses";

function LevelChip({ level }: { level: number }) {
	return (
		<span class="mr-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[2px] bg-carmesim-600 px-1 text-[11px] font-extrabold text-white">
			{level}
		</span>
	);
}

// An unmade race choice: the line says so, and whoever may edit gets the way to the Oficina's race step.
function PendingChoice({ url, labels }: { url?: string; labels: Labels }) {
	const edits = useContext(EditContext);
	return (
		<p class="text-ink-muted">
			{labels.abilities.choice_pending}
			{edits && url && (
				<>
					{" · "}
					<a class="font-bold text-link hover:text-link-hover" href={url}>
						{labels.abilities.choose}
					</a>
				</>
			)}
		</p>
	);
}

// A daily ability carries its name and use boxes ahead of the text; any other ability's text opens with its name.
function AbilityRow({ ability, spent, labels, choicesUrl }: { ability: LiveSheetAbility; spent: Spent; labels: Labels; choicesUrl?: string }) {
	return (
		<>
			{ability.uses.length > 0 && (
				<div class="flex flex-wrap items-center gap-1">
					<LevelChip level={ability.level} />
					<b>{ability.name}:</b>
					<Uses name={ability.name} uses={ability.uses} spent={spent} labels={labels} />
				</div>
			)}
			<RuleText html={ability.html} name={ability.name} rollable={ability.chance == null} />
			{ability.extra_html.map((text) => (
				<RuleText key={text} html={text} name={ability.name} rollable={ability.chance == null} class="mt-1" />
			))}
			{ability.choice_pending && <PendingChoice url={choicesUrl} labels={labels} />}
		</>
	);
}

export function RaceAbilities({ view, labels, choicesUrl }: { view: SheetView; labels: Labels; choicesUrl?: string }) {
	const { sheet } = view;
	const spent = spentSet(view);
	const abilities = view.abilities.filter((ability) => ability.source === "race");
	const infravision = [sheet.infravision > 0 ? `${sheet.infravision} m` : null, sheet.infravision_notes].filter(Boolean).join(". ");
	return (
		<Box icon="race" title={labels.boxes.race_abilities} fold={foldOf(view, "race", labels.boxes.race_abilities, labels)} class="px-3.5 pb-3">
			<ul class="flex flex-col gap-2 text-[15px]">
				{infravision && (
					<li class="flex items-center gap-2">
						<Icon name="infravision" class="h-4 w-4 text-ink-muted" />
						<span>
							<b>{labels.abilities.infravision}</b> {infravision}
						</span>
					</li>
				)}
				{abilities.map((ability) => (
					<li key={ability.id}>
						<AbilityRow ability={ability} spent={spent} labels={labels} choicesUrl={choicesUrl} />
					</li>
				))}
				{!infravision && abilities.length === 0 && <li class="italic text-ink-muted">{labels.abilities.empty_race}</li>}
			</ul>
		</Box>
	);
}

function Talents({ ability, view, labels }: { ability: LiveSheetAbility; view: SheetView; labels: Labels }) {
	return (
		<ul class="cs-talents">
			{talentTiles(ability.talents, view.derived.rogue_talents, labels).map(({ talent, total, lines }) => {
				const icon = talentIconUrl(talent.key);
				const { title, html } = labels.rogue_talents[talent.key] ?? { title: talent.key, html: "" };
				return (
					<li key={talent.key} class="flex flex-col items-center gap-1.5 rounded-lg border border-line bg-surface px-1 pb-2.5 pt-2">
						<div class="flex flex-wrap items-center justify-center gap-1.5 text-[15px]">
							{icon && <MaskIcon url={icon} class="h-[18px] w-[18px] text-ink-muted" />}
							<Term label={title} title={title} lines={lines} />
						</div>
						{total != null && (
							<RollChip size="sm" spec={testRoll(interpolate(labels.descriptions.talent, { talent: title }), total, "1d6")}>
								{interpolate(labels.abilities.talent_chance, { n: total })}
							</RollChip>
						)}
						{html && <div class="px-1.5 text-center text-[13px] leading-snug text-ink-muted" dangerouslySetInnerHTML={{ __html: html }} />}
					</li>
				);
			})}
		</ul>
	);
}

// A circle of rituals: its use boxes, then the spells they cast. A closed list names them, each opening its text; an open one is
// the spell index, filtered to the school and circle.
function RitualCircle({ ritual, ability, spent, labels }: { ritual: LiveSheetRitual; ability: LiveSheetAbility; spent: Spent; labels: Labels }) {
	const name = interpolate(labels.abilities.rituals, { name: ability.name, circle: ritual.circle });
	// The text comes from the editor's picker endpoint, so a viewer who may not edit gets the names alone.
	const editor = useContext(EditContext) !== null;
	return (
		<div class="flex flex-col gap-1">
			<div class="flex flex-wrap items-center gap-2">
				<b>{name}</b>
				<Uses name={name} uses={ritual.uses} spent={spent} labels={labels} />
			</div>
			{ritual.spells.length > 0 && (
				<ul class="flex flex-wrap gap-x-3 gap-y-0.5 pl-1">
					{ritual.spells.map((spell) => (
						<li key={spell.spell_id}>
							{editor ? <SpellName spell={{ spell_id: spell.spell_id, circle: ritual.circle, name: spell.name }} preview /> : spell.name}
						</li>
					))}
				</ul>
			)}
			{ritual.list_url && (
				<a href={ritual.list_url} target="_blank" rel="noopener" class="cs-tap gap-1 self-start pl-1 font-bold text-link hover:text-link-hover">
					{interpolate(labels.abilities[`ritual_list_${ritual.list_school}`] ?? "", { circle: ritual.circle })}
					<Icon name="externalLink" class="h-3 w-3" />
				</a>
			)}
		</div>
	);
}

function ClassAbility({ ability, view, spent, labels }: { ability: LiveSheetAbility; view: SheetView; spent: Spent; labels: Labels }) {
	const choice = choiceText(ability, labels);
	return (
		<li class="flex flex-col gap-1.5">
			<AbilityRow ability={ability} spent={spent} labels={labels} />
			{choice && (
				<div class="flex items-center gap-2">
					<Icon name="weaponMastery" class="h-4 w-4 text-ink-muted" />
					<span>
						<b>{labels.abilities.choices}</b> {choice}
					</span>
				</div>
			)}
			{ability.chance != null && (
				<div class="flex flex-wrap items-center gap-2">
					<b>{interpolate(labels.abilities.chance_of, { name: ability.name.toLocaleLowerCase() })}</b>
					<RollChip size="sm" spec={testRoll(ability.name, ability.chance, "1d6")}>
						{interpolate(labels.abilities.talent_chance, { n: ability.chance })}
					</RollChip>
				</div>
			)}
			{ability.multiplier != null && <p class="text-[14px] text-ink-muted">{interpolate(labels.abilities.multiplier, { n: ability.multiplier })}</p>}
			{ability.talents.length > 0 && <Talents ability={ability} view={view} labels={labels} />}
			{ability.rituals.map((ritual) => (
				<RitualCircle key={ritual.circle} ritual={ritual} ability={ability} spent={spent} labels={labels} />
			))}
			<Spells ability={ability} view={view} spent={spent} labels={labels} />
		</li>
	);
}

// What the character still owes from its levels (`derived.pending`), for a viewer who has Level Up: the popup resolves a level's,
// and the race's choices keep to the race step.
function PendingBand({ view, labels, choicesUrl }: { view: SheetView; labels: Labels; choicesUrl?: string }) {
	const open = useOpenPopup();
	const words = labels.level_up;
	const items = view.derived.pending;
	const first = firstPendingLevel(items);
	const race = items.some(isRace);
	return (
		<div
			role="status"
			class="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-dourado-600 bg-dourado-100 px-3 py-2 text-[15px] text-grafite-700 dark:border-dourado-700 dark:bg-dourado-900 dark:text-ink"
		>
			<Icon name="warning" class="h-4 w-4 shrink-0" />
			<span class="min-w-0 flex-1 basis-56">
				<b>{interpolate(words[items.length === 1 ? "pending_count_one" : "pending_count_other"], { count: items.length })}:</b> {pendingList(items, words)}
			</span>
			{open && first !== null && (
				<Button tone="alt" size="sm" icon="levelUp" label={words.resolve} onClick={(button) => open({ kind: "level_up", level: first }, button)} />
			)}
			{race && choicesUrl && (
				<a class="font-bold text-link hover:text-link-hover" href={choicesUrl}>
					{words.resolve_race}
				</a>
			)}
			<HelpLink href={labels.help?.pending} label={labels.help?.link} />
		</div>
	);
}

// A class that casts but whose spells ability is not reached yet (an Elfo Aventureiro below the 3rd level): the pickers stay on
// offer, since the table may let it memorize early (the closed circles say when they open).
function EarlyCasting({ spellbook, labels }: { spellbook: boolean; labels: Labels }) {
	const open = useOpenPopup();
	if (!open) return null;
	return (
		<div class="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
			<p class="min-w-0 flex-1 basis-56 text-[15px] text-ink-muted">{labels.spells_picker.early_hint}</p>
			<Button tone="alt" size="sm" icon="spellBook" label={labels.spells_picker.prepare} onClick={(button) => open({ kind: "spells" }, button)} />
			{spellbook && (
				<Button tone="alt" size="sm" icon="pen" label={labels.spells_picker.grimoire_edit} onClick={(button) => open({ kind: "grimoire" }, button)} />
			)}
		</div>
	);
}

export function ClassAbilities({ view, labels, choicesUrl }: { view: SheetView; labels: Labels; choicesUrl?: string }) {
	const edits = useContext(EditContext);
	const spent = spentSet(view);
	const abilities = view.abilities.filter((ability) => ability.source === "class");
	// What a class still owes shows even when it has no ability to list.
	const pending = edits && view.derived.pending.length > 0;
	const casting = edits && !abilities.some((ability) => ability.casts) ? view.derived.casting : null;
	if (abilities.length === 0 && !pending && !casting) return null;
	return (
		<Box icon="class" title={labels.boxes.class_abilities} fold={foldOf(view, "class", labels.boxes.class_abilities, labels)} class="px-3.5 pb-3.5">
			{pending && <PendingBand view={view} labels={labels} choicesUrl={choicesUrl} />}
			{casting && <EarlyCasting spellbook={casting.spellbook} labels={labels} />}
			{abilities.length > 0 && (
				<ul class="flex flex-col gap-2.5 text-[15px]">
					{abilities.map((ability) => (
						<ClassAbility key={ability.id} ability={ability} view={view} spent={spent} labels={labels} />
					))}
				</ul>
			)}
		</Box>
	);
}
